import React, { useEffect, useMemo, useState } from 'react';
import { PurchaseOrder } from '../types';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { formatDate } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import './Entry.css';
import './Sections.css';

interface Line {
  productId: string;
  name: string;
  qty: number;
  price: number;
}
interface Pending {
  productId: string;
  name: string;
  qty: string;
  price: string;
}

// Matches activity_purchase_order_activity.xml — supplier header + search + Qty/Price/Total entry + item table.
const OrderRequest: React.FC = () => {
  const { products, suppliers, receiveGoods, formatMoney } = usePos();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  useEffect(() => {
    db.getOrders().then(setOrders);
  }, []);
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [supplierId, setSupplierId] = useState('');

  const supplier = suppliers.find((s) => s.id === supplierId);

  const findProduct = (term: string) =>
    products.find((p) => p.barcode === term || String(p.id) === term || p.name.toLowerCase() === term.toLowerCase());

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    const p = findProduct(search.trim());
    if (p) {
      setPending({ productId: p.id, name: p.name, qty: '1', price: String(p.costPrice ?? 0) });
      setSearch('');
    }
  };

  const pendingTotal = pending ? (parseFloat(pending.qty) || 0) * (parseFloat(pending.price) || 0) : 0;

  const commit = () => {
    if (!pending) return;
    const qty = parseFloat(pending.qty) || 0;
    const price = parseFloat(pending.price) || 0;
    if (qty <= 0) return;
    setLines((prev) => {
      const f = prev.find((l) => l.productId === pending.productId);
      if (f) return prev.map((l) => (l.productId === pending.productId ? { ...l, qty: l.qty + qty, price } : l));
      return [...prev, { productId: pending.productId, name: pending.name, qty, price }];
    });
    setPending(null);
  };

  const removeLine = (id: string) => setLines((prev) => prev.filter((l) => l.productId !== id));
  const total = lines.reduce((s, l) => s + l.qty * l.price, 0);

  const persist = (next: PurchaseOrder[]) => {
    db.saveOrders(next);
    setOrders(next);
  };

  const createOrder = () => {
    if (!lines.length) return;
    const order: PurchaseOrder = {
      id: db.newId(),
      orderNo: `PO${Date.now()}`,
      supplierId: supplierId || undefined,
      supplierName: supplier?.name,
      items: lines.map((l) => ({ productId: l.productId, name: l.name, quantity: l.qty, costPrice: l.price })),
      total,
      status: 'pending',
      timestamp: new Date().toISOString(),
    };
    persist([...orders, order]);
    setLines([]);
    alert('Purchase order created.');
  };

  const receiveOrder = (o: PurchaseOrder) => {
    if (o.status === 'received') return;
    receiveGoods(o.items.map((i) => ({ productId: i.productId, quantity: i.quantity })), o.supplierId, o.orderNo);
    persist(orders.map((x) => (x.id === o.id ? { ...x, status: 'received' } : x)));
    alert(`${o.orderNo} received — stock updated.`);
  };

  // Print the current order draft as a thermal slip (ESC/POS), falling back to
  // the browser print dialog.
  const printSlip = () => {
    if (!lines.length) return;
    const rows: (string | number)[][] = lines.map((l) => [l.name, l.qty, l.price.toFixed(2), (l.qty * l.price).toFixed(2)]);
    rows.push(['', '', 'TOTAL', total.toFixed(2)]);
    printReportAuto('Order Request', supplier ? supplier.name : 'No supplier', ['Item', 'Qty', 'Price', 'Total'], rows);
  };

  const ordered = useMemo(() => [...orders].reverse(), [orders]);

  return (
    <div className="es-page">
      <div className="es-head">
        <span>Order Request (GR)</span>
        <span className="es-sub">{supplier ? `${supplier.name} · ${supplier.phone ?? ''}` : 'No supplier selected'}</span>
      </div>

      <div className="es-toolbar">
        <div className="es-search" style={{ flex: 1 }}>
          <input placeholder="Search by Code/Barcode/Name" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onSearchKey} />
          <div className="es-search-btn">🔍</div>
        </div>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={{ minWidth: 180, padding: 10 }}>
          <option value="">Supplier…</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* entry: Qty | Price | Total */}
      <div className="es-entry">
        <input className="es-ebox" placeholder="Qty" value={pending?.qty ?? ''} onChange={(e) => pending && setPending({ ...pending, qty: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && commit()} />
        <input className="es-ebox" placeholder="Price" value={pending?.price ?? ''} onChange={(e) => pending && setPending({ ...pending, price: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && commit()} />
        <div className="es-ebox total" onClick={commit} title="Add to order">
          {pendingTotal.toFixed(2)}
        </div>
      </div>

      {/* item table: Item name | Quantity | Price | Total */}
      <div className="es-items">
        <div className="es-item" style={{ fontWeight: 700, color: 'var(--color-primary-darker)', borderBottom: '2px solid var(--color-border)' }}>
          <span className="es-item-name">Item name</span>
          <span style={{ width: 60, textAlign: 'center' }}>Quantity</span>
          <span style={{ width: 80, textAlign: 'right' }}>Price</span>
          <span className="es-item-total">Total</span>
        </div>
        {lines.length === 0 ? (
          <div className="es-empty">{pending ? `Adding ${pending.name}` : 'Search a product to add to the order'}</div>
        ) : (
          lines.map((l) => (
            <div className="es-item" key={l.productId}>
              <span className="es-del" onClick={() => removeLine(l.productId)}>
                ✕
              </span>
              <span className="es-item-name">{l.name}</span>
              <span style={{ width: 60, textAlign: 'center' }} className="es-orange">{l.qty}</span>
              <span style={{ width: 80, textAlign: 'right' }}>{l.price.toFixed(2)}</span>
              <span className="es-item-total">{(l.qty * l.price).toFixed(2)}</span>
            </div>
          ))
        )}
      </div>

      <div className="es-bottom">
        <div className="es-lastbill">Items : {lines.length}</div>
        <div className="es-netbar">ORDER TOTAL : {total.toFixed(2)}</div>
      </div>

      <div className="es-actions">
        <button className="es-btn-orange" onClick={createOrder} disabled={!lines.length}>
          Create Order
        </button>
        <button className="es-btn-grey" onClick={printSlip} disabled={!lines.length}>
          Print
        </button>
      </div>

      {/* existing orders */}
      <div className="section-card" style={{ marginTop: 4 }}>
        <h2>Orders ({ordered.length})</h2>
        {ordered.length === 0 ? (
          <p className="muted">No purchase orders yet.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Order No</th>
                <th>Date</th>
                <th>Supplier</th>
                <th className="num-cell">Items</th>
                <th className="right">Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((o) => (
                <tr key={o.id}>
                  <td>{o.orderNo}</td>
                  <td>{formatDate(o.timestamp)}</td>
                  <td>{o.supplierName ?? '—'}</td>
                  <td>{o.items.length}</td>
                  <td className="right">{formatMoney(o.total)}</td>
                  <td>
                    <span className={`badge ${o.status === 'received' ? 'in' : 'out'}`}>{o.status}</span>
                  </td>
                  <td>{o.status === 'pending' && <button className="btn-teal" onClick={() => receiveOrder(o)}>Receive</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OrderRequest;
